"use client";

import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { App } from "antd";
import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/context/AuthContext";

import { getSearchParams, updateURLParameter } from "@/utils/helpers";

import PageLoader from "@/src/components/Common/PageLoader";

export default function AuthProtectedRoutesLayout({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently, user } = useAuth0();
  const { setExpiredSessionError } = useAuth();
  const { message } = App.useApp();
  const pathname = usePathname();
  const { t } = useTranslation();
  const [token, setToken] = useState<string>();

  useEffect(() => {
    const checkUserIsAuthenticated = async () => {
      const params = getSearchParams(document.location.search);
      const isMobile = params ? params.has("mobile") : false;
      const mobileUserId = isMobile ? params.get("mobile") : "";
      const curUserId = user?.app_teamdeskId;

      // Remove Auth0 data from localStorage if the mobile user is different from the web user
      if (isMobile && curUserId && curUserId !== mobileUserId) {
        
        // Updated to old curUserId just to prevent endless loading
        updateURLParameter(window.location.href, { mobile: curUserId }, true);

        // Remove Auth0 data from localStorage
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith("@@auth0spajs@@")) {
            localStorage.removeItem(key);
          }
        });

        window.location.reload();
      }

      if (isAuthenticated || isMobile) {
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.AUTH0_AUDIENCE,
              scope: "read:current_user",
            },
          });

          setToken(token);
        } catch {
          if (token) setToken(undefined);
          setExpiredSessionError(true);
        }
      } else {
        const error = t("common.messages.unavailablePage");
        message.error(error);
        await loginWithRedirect();

        throw new Error(error);
      }
    };

    checkUserIsAuthenticated();
  }, [pathname]);

  return isAuthenticated && token ? <>{children}</> : <PageLoader />;
}
